import Checkbox from 'components/forms/Checkbox'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ResourceDto from 'resource/dto/ResourceDto'
import DtoDistributionUnit from 'distributionUnit/dto/DtoDistributionUnit'
import DtoHydrometricStation from 'hydrometry/dto/DtoHydrometricStation'
import DtoInstallation from 'installation/dto/installation/DtoInstallation'
import DtoPiezometerLight from 'piezometry/dto/DtoPiezometerLight'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import { ProductionUnitShape } from 'productionUnit/shapes/ProductionUnitShapes'
import DtoQualitometerLight from 'quality/dto/DtoQualitometerLight'
import {
    DISTRIBUTION,
    HYDRO,
    INSTALLATION,
    PIEZO,
    PLUVIO,
    PRODUCTION,
    QUALITO,
    RESOURCE,
    CATCHMENT,
} from 'administration/components/user/constants/ApplicationHabilitationConstants'
import DtoUserStation from 'administration/components/user/dto/DtoUserStation'
import i18n from 'simple-react-i18n'
import SelectionTableModal from 'components/modal/SelectionTableModal'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import Input from 'components/forms/Input'
import { getStationTypeFromUserConstants } from 'utils/StationUtils'
import { searchAllCharacters } from 'utils/StringUtil'
import { SINGLE } from 'administration/components/user/constants/UserStationConstants'
import UserAction from 'administration/components/user/actions/UserAction'
import { Accordion, AccordionDetails, Grid } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import useListIndexed from 'utils/customHook/useListIndexed'
import { AccordionTitle } from 'components/styled/Accordions'
import ProgressCard from '../../../../../../components/card/ProgressCard'
import { ModuleHabsContext, ModuleHabsDispatchContext } from 'administration/components/modules/AdminModulesApp'
import DtoUserHabilitation from 'administration/components/user/dto/DtoUserHabilitation'

const SelectedStationDialog = ({
    isOpen = false,
    setOpen = () => { },
    stations = [],
    defaultSelected = [],
    stationType = '',
}) => {
    const dispatch = useDispatch()
    const {
        userStations,
        user,
    } = useSelector(store => ({
        userStations: store.UserReducer.userStations,
        user: store.UserReducer.user,
    }), shallowEqual)

    const moduleHabs = useContext(ModuleHabsContext)
    const moduleHabsDispatch = useContext(ModuleHabsDispatchContext)

    const stationsOfUser = moduleHabs?.stationsUser ?? userStations
    const onClose = () => {
        setOpen(false)
    }

    return isOpen && (
        <SelectionTableModal
            isOpen={isOpen}
            onClose={onClose}
            onValidate={newStationHabilitation => {
                const otherHabs = stationsOfUser.filter(s => s.stationType !== stationType)
                const selectedStationHabs = newStationHabilitation.map(id => {
                    return new DtoUserStation({
                        stationCode: `${id}`,
                        stationType,
                        login: user.login,
                        selectedMode: SINGLE,
                    })
                })
                const newStationsHabs = [...otherHabs, ...selectedStationHabs]
                if (moduleHabs?.habilitationsUser || moduleHabs?.stationsUser) {
                    moduleHabsDispatch({ type: 'stationsUser', payload: newStationsHabs })
                    onClose()
                } else {
                    dispatch(UserAction.updateUserStations(user.login, newStationsHabs)).then(() => {
                        onClose()
                    })
                }
            }}
            title={`${i18n.selectStationsOfType} ${i18n[getStationTypeFromUserConstants(stationType)].toLowerCase()}`}

            listData={stations}
            listHeaders={['code', 'city', 'name']}
            listTitle={i18n.nonSelectedStations}

            defaultSelectionList={defaultSelected}
            selectionListHeaders={['code', 'city', 'name']}
            selectionListTitle={i18n.selectedStations}

            maxHeightTable={'50vh'}
            filterField={({
                filter,
                setFilter,
            }) => (
                <Grid container spacing={1} style={{ paddingTop: '5px' }}>
                    <Grid size={6}>
                        <SimpleFilterSelect
                            stationType={getStationTypeFromUserConstants(stationType)}
                            onChange={(resultFilter, newFilter) => {
                                setFilter(prev => ({ ...prev, resultFilter, filter: newFilter }))
                            }}
                            stations={stations}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.search}
                            value={filter.searchValue}
                            onChange={searchValue => setFilter(prev => ({ ...prev, searchValue }))}
                        />
                    </Grid>
                </Grid>
            )}
            filterFunction={(list, { searchValue, resultFilter = [], filter }) => {
                const searchValueFormat = searchAllCharacters(searchValue)
                const filterSearchValue = searchValue ? list.filter(p => searchAllCharacters(['code', 'city', 'name'].map(key => p[key])).includes(searchValueFormat)) : list
                return filter !== -1 ? resultFilter.map(s => filterSearchValue.find(e => e.id === s.id)).filter(e => !!e) : filterSearchValue
            }}
        />
    )
}

SelectedStationDialog.propTypes = {
    isOpen: PropTypes.bool,
    setOpen: PropTypes.func,
    stationType: PropTypes.oneOf([PIEZO, QUALITO, HYDRO, PLUVIO, PRODUCTION, DISTRIBUTION, INSTALLATION, CATCHMENT, RESOURCE]),
    defaultSelected: PropTypes.arrayOf(PropTypes.number),
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoInstallation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoDistributionUnit)),
        PropTypes.arrayOf(ProductionUnitShape),
        PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(ResourceDto)),
        // add catchment
    ]),

    stationsUser: PropTypes.arrayOf(PropTypes.shape({})),
    setStationsUser: PropTypes.func,
}

const BasicHabilitationAccordion = ({
    stationType = '',
    title = '',
    stations = [],
    isLoaded = false,
}) => {
    const dispatch = useDispatch()
    const {
        userStations,
        cities,
        userHabilitations,
        user,
    } = useSelector(store => ({
        userStations: store.UserReducer.userStations,
        cities: store.CityReducer.cities,
        userHabilitations: store.UserReducer.userHabilitations,
        user: store.UserReducer.user,
    }), shallowEqual)

    const moduleHabs = useContext(ModuleHabsContext)
    const moduleHabsDispatch = useContext(ModuleHabsDispatchContext)

    useEffect(() => {
        if (!!moduleHabs && !moduleHabs.habilitationsUser?.length) {
            moduleHabsDispatch({ type: 'habilitationsUser', payload: userHabilitations })
        }
    }, [userHabilitations])

    useEffect(() => {
        if (!!moduleHabs && !moduleHabs.stationsUser?.length) {
            moduleHabsDispatch({ type: 'stationsUser', payload: userStations })
        }
    }, [userStations])

    const habilitationsOfUser = useMemo(() => moduleHabs?.habilitationsUser ?? userHabilitations, [moduleHabs?.habilitationsUser, userHabilitations])
    const stationsOfUser = useMemo(() => moduleHabs?.stationsUser ?? userStations, [moduleHabs?.stationsUser, userStations])

    const indexedCities = useListIndexed(cities, 'code')

    const stationHabilitations = useMemo(() => {
        const habFiltered = stationsOfUser.filter(s => s.stationType === stationType)
        return habFiltered.map(h => parseInt(h.stationCode))
    }, [stationType, stationsOfUser])

    const isAllSelected = useMemo(() => {
        return habilitationsOfUser.some(hab => hab.habilitation === `${stationType}_ALL`)
    }, [stationType, habilitationsOfUser])

    const getListHabilitationStation = (isCheck) => {
        if (isCheck) {
            return stationsOfUser.filter(s => s.selectedMode === SINGLE && s.stationType !== stationType)
        } else if (moduleHabs?.habilitationsUser || moduleHabs?.stationsUser) {
            return userStations
        }
        return stationsOfUser
    }

    const getListHabilitation = (isCheck) => {
        const habilitation = `${stationType}_ALL`
        if (!isCheck) {
            return habilitationsOfUser.filter(hab => hab.habilitation !== habilitation)
        }
        return [
            ...habilitationsOfUser,
            {
                login: user.login,
                habilitation,
            },
        ]
    }

    const [isOpen, setOpen] = useState(false)

    const stationsFormated = useMemo(() => {
        return stationHabilitations.map(id => {
            const station = stations.find(s => s.id == id)
            if (!station) {
                return {}
            }
            const code = station.designation ? `${station.code}/${station.designation}` : station.code
            const cityFound = station.townCode ? indexedCities[station.townCode] : undefined
            const city = cityFound ? `${cityFound.name} - [${cityFound.code}]` : ''
            return {
                id: station.id,
                code,
                name: station.name,
                city,
            }
        }).filter(s => !!s)
    }, [indexedCities, stationHabilitations, stations])

    const valueSelected = `(${stationHabilitations.length}/${stations.length})`

    const stationActions = [{
        icon: 'edit',
        onClick: () => setOpen(true),
    }]

    return (
        <>
            <Accordion defaultExpanded={false}>
                <AccordionTitle
                    title={title}
                />
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid size='auto'>
                            <Checkbox
                                checked={isAllSelected}
                                label={i18n.allStations}
                                onChange={isCheck => {
                                    const listHabilitation = getListHabilitation(isCheck)
                                    const listHabilitationStation = getListHabilitationStation(isCheck)

                                    if (moduleHabs?.habilitationsUser || moduleHabs?.stationsUser) {
                                        moduleHabsDispatch({ type: 'stationsHabilitationsUser', payload: { habilitationsUser: listHabilitation, stationsUser: listHabilitationStation } })
                                    } else {
                                        dispatch(UserAction.updateUserHabilitations(user.login, listHabilitation))
                                        dispatch(UserAction.updateUserStations(user.login, listHabilitationStation))
                                    }
                                }}
                            />
                        </Grid>
                        { (!isAllSelected && isLoaded) && (
                            <Grid size={12}>
                                <CardTable
                                    title={`${i18n.selectedStations} ${valueSelected}`}
                                    hideNbElements
                                    actions={stationActions}

                                    rows={stationsFormated}
                                    headers={['code', 'name', 'city']}
                                />
                            </Grid>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>
            <SelectedStationDialog
                isOpen={isOpen}
                setOpen={setOpen}
                stationType={stationType}
                stations={stations}
                defaultSelected={stationHabilitations}
            />
        </>
    )
}

BasicHabilitationAccordion.propTypes = {
    stationType: PropTypes.oneOf([PIEZO, QUALITO, PLUVIO, HYDRO, PRODUCTION, DISTRIBUTION, INSTALLATION, CATCHMENT, RESOURCE]),
    title: PropTypes.string,
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoInstallation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoDistributionUnit)),
        PropTypes.arrayOf(ProductionUnitShape),
        PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(ResourceDto)),
        // add catchment
    ]),
    isLoaded: PropTypes.bool,
}

const HabilitationAccordion = ({
    stationType = '',
    title = '',
    stations = [],
    noNetworkDisplay = false,
    isLoaded = false,
}) => {
    const dispatch = useDispatch()
    const {
        networks,
        contributors,
        userHabilitations,
        userStations,
        cities,
        user,
    } = useSelector(store => ({
        networks: store.NetworkReducer.networks,
        contributors: store.ContributorReducer.contributors,
        userHabilitations: store.UserReducer.userHabilitations,
        userStations: store.UserReducer.userStations,
        cities: store.CityReducer.cities,
        user: store.UserReducer.user,
    }), shallowEqual)

    const moduleHabs = useContext(ModuleHabsContext)
    const moduleHabsDispatch = useContext(ModuleHabsDispatchContext)

    useEffect(() => {
        if (!!moduleHabs && !moduleHabs.habilitationsUser?.length) {
            moduleHabsDispatch({ type: 'habilitationsUser', payload: userHabilitations })
        }
    }, [userHabilitations])

    useEffect(() => {
        if (!!moduleHabs && !moduleHabs.stationsUser?.length) {
            moduleHabsDispatch({ type: 'stationsUser', payload: userStations })
        }
    }, [userStations])

    const habilitationsOfUser = useMemo(() => moduleHabs?.habilitationsUser ?? userHabilitations, [moduleHabs?.habilitationsUser, userHabilitations])
    const stationsOfUser = useMemo(() => moduleHabs?.stationsUser ?? userStations, [moduleHabs?.stationsUser, userStations])

    const contributorHabilitations = useMemo(() => {
        const contributorHabsFiltered = habilitationsOfUser.filter(c => c.habilitation.startsWith(`${stationType}_CONTRIB_`))
        return contributorHabsFiltered.map(n => parseInt(n.habilitation.split(`${stationType}_CONTRIB_`)[1]))
    }, [stationType, habilitationsOfUser])

    const networkHabilitations = useMemo(() => {
        const networkHabsFiltered = habilitationsOfUser.filter(n => n.habilitation.startsWith(`${stationType}_NETWORK_`))
        return networkHabsFiltered.map(n => parseInt(n.habilitation.split(`${stationType}_NETWORK_`)[1]))
    }, [stationType, habilitationsOfUser])

    const stationHabilitations = useMemo(() => {
        const habFiltered = stationsOfUser.filter(s => s.stationType === stationType)
        return habFiltered.map(h => parseInt(h.stationCode))
    }, [stationType, stationsOfUser])

    const isAllSelected = useMemo(() => {
        return habilitationsOfUser.some(hab => hab.habilitation === `${stationType}_ALL`)
    }, [stationType, habilitationsOfUser])

    const getListHabilitationStation = (isCheck) => {
        if (isCheck) {
            return stationsOfUser.filter(s => s.selectedMode === SINGLE && s.stationType !== stationType)
        } else if (moduleHabs?.habilitationsUser || moduleHabs?.stationsUser) {
            return userStations
        }
        return stationsOfUser
    }

    const getListHabilitation = (isCheck) => {
        const habilitation = `${stationType}_ALL`
        if (!isCheck) {
            return habilitationsOfUser.filter(hab => hab.habilitation !== habilitation)
        }
        return [
            ...habilitationsOfUser,
            new DtoUserHabilitation({
                login: user.login,
                habilitation,
            }),
        ]
    }

    const [isDialogStationOpen, setDialogStationOpen] = useState(false)
    const [isDialogContributorOpen, setDialogContributorOpen] = useState(false)
    const [isDialogNetworkOpen, setDialogNetworkOpen] = useState(false)

    const contributorsFormated = contributorHabilitations.map(id => contributors.find(n => n.id === id)).filter(s => !!s)

    const networksFormated = networkHabilitations.map(id => networks.find(n => n.id === id)).filter(s => !!s)

    const stationsFormated = useMemo(() => {
        return stationHabilitations.map(id => {
            const station = stations.find(s => s.id == id)
            if (!station) {
                return undefined
            }
            const code = station.designation ? `${station.code}/${station.designation}` : station.code
            const cityFound = station.townCode ? cities.find(c => c.code === station.townCode) : undefined
            const city = cityFound ? `${cityFound.name} - [${cityFound.code}]` : ''
            return {
                id: station.id,
                code,
                name: station.name,
                city,
            }
        }).filter(s => !!s)
    }, [cities, stationHabilitations, stations])

    const valueSelected = `(${stationsFormated.length}/${stations.length})`
    const contributorsSelected = `(${contributorsFormated.length}/${contributors.length})`
    const networksSelected = `(${networksFormated.length}/${networks.length})`

    const stationActions = [{
        icon: 'edit',
        onClick: () => setDialogStationOpen(true),
    }]
    const contributorActions = [{
        icon: 'edit',
        onClick: () => setDialogContributorOpen(true),
    }]
    const networkActions = [{
        icon: 'edit',
        onClick: () => setDialogNetworkOpen(true),
    }]

    return (
        <>
            <Accordion defaultExpanded={false}>
                <AccordionTitle
                    title={title}
                />
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid size='auto'>
                            <Checkbox
                                checked={isAllSelected}
                                label={i18n.allStations}
                                onChange={isCheck => {
                                    const listHabilitation = getListHabilitation(isCheck)
                                    const listHabilitationStation = getListHabilitationStation(isCheck)

                                    if (moduleHabs?.habilitationsUser || moduleHabs?.stationsUser) {
                                        moduleHabsDispatch({ type: 'stationsHabilitationsUser', payload: { habilitationsUser: listHabilitation, stationsUser: listHabilitationStation } })
                                    } else {
                                        dispatch(UserAction.updateUserHabilitations(user.login, listHabilitation))
                                        dispatch(UserAction.updateUserStations(user.login, listHabilitationStation))
                                    }
                                }}
                            />
                        </Grid>
                        {!isAllSelected && (
                            <>
                                { isLoaded ? (
                                    <>
                                        <Grid size={12}>

                                            <CardTable
                                                title={`${i18n.selectedStations} ${valueSelected}`}
                                                hideNbElements
                                                actions={stationActions}
                                                rows={stationsFormated}
                                                headers={['code', 'name', 'city']}
                                            />

                                        </Grid>
                                        <Grid size={12}>
                                            <CardTable
                                                title={`${i18n.contributors} ${contributorsSelected}`}
                                                hideNbElements
                                                actions={contributorActions}

                                                rows={contributorsFormated}
                                                headers={['siret', 'name', 'mnemonique', 'postcode', 'city', 'phoneTel', 'domain', 'sandre', 'status']}
                                            />
                                        </Grid>
                                        {!noNetworkDisplay && (
                                            <Grid size={12}>
                                                <CardTable
                                                    title={`${i18n.networks} ${networksSelected}`}
                                                    hideNbElements
                                                    actions={networkActions}

                                                    rows={networksFormated}
                                                    headers={['code', 'name', 'mnemonic', 'finality', 'dce', 'sandreCode']}
                                                />
                                            </Grid>
                                        )}
                                    </>) : (
                                    <ProgressCard indeterminate={true} />
                                )}
                            </>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>
            <SelectedStationDialog
                isOpen={isDialogStationOpen}
                setOpen={setDialogStationOpen}
                stationType={stationType}
                stations={stations}
                defaultSelected={stationHabilitations}
            />
            <SelectionTableModal
                isOpen={isDialogContributorOpen}
                onClose={() => setDialogContributorOpen(false)}
                onValidate={newContributorHabilitation => {
                    const otherHabs = habilitationsOfUser.filter(s => !s.habilitation.startsWith(`${stationType}_CONTRIB_`))
                    const contributorsHab = newContributorHabilitation.map(id => ({
                        login: user.login,
                        habilitation: `${stationType}_CONTRIB_${id}`,
                    }))
                    const newContribHabs = [...otherHabs, ...contributorsHab]
                    if (moduleHabs?.habilitationsUser || moduleHabs?.stationsUser) {
                        moduleHabsDispatch({ type: 'habilitationsUser', payload: newContribHabs })
                    } else {
                        dispatch(UserAction.updateUserHabilitations(user.login, newContribHabs)).then(() => setDialogContributorOpen(false))
                    }
                }}
                title={i18n.selectContributors}

                listData={contributors}
                listHeaders={['siret', 'name', 'mnemonique', 'phoneTel']}
                listTitle={i18n.contributors}

                defaultSelectionList={contributorHabilitations}
                selectionListHeaders={['siret', 'name', 'mnemonique', 'phoneTel']}
                selectionListTitle={i18n.selectedContributors}

                maxHeightTable={'50vh'}
                filterField={({
                    filter,
                    setFilter,
                }) => (
                    <Grid container spacing={'10px'} style={{ paddingTop: '5px' }}>
                        <Grid size={6}>
                            <Input
                                title={i18n.search}
                                value={filter.searchValue}
                                onChange={searchValue => setFilter(prev => ({ ...prev, searchValue }))}
                            />
                        </Grid>
                    </Grid>
                )}
                filterFunction={(list, { searchValue }) => {
                    const searchValueFormat = searchAllCharacters(searchValue)
                    return searchValue ? list.filter(p => searchAllCharacters(['siret', 'name', 'mnemonique', 'phoneTel'].map(key => p[key])).includes(searchValueFormat)) : list
                }}
            />
            <SelectionTableModal
                isOpen={isDialogNetworkOpen}
                onClose={() => setDialogNetworkOpen(false)}
                onValidate={newContributorHabilitation => {
                    const otherHabs = habilitationsOfUser.filter(s => !s.habilitation.startsWith(`${stationType}_NETWORK_`))
                    const networkHab = newContributorHabilitation.map(id => ({
                        login: user.login,
                        habilitation: `${stationType}_NETWORK_${id}`,
                    }))
                    const newNetworkHabs = [...otherHabs, ...networkHab]
                    if (moduleHabs?.habilitationsUser || moduleHabs?.stationsUser) {
                        moduleHabsDispatch({ type: 'habilitationsUser', payload: newNetworkHabs })
                    } else {
                        dispatch(UserAction.updateUserHabilitations(user.login, newNetworkHabs)).then(() => setDialogNetworkOpen(false))
                    }
                }}
                title={i18n.selectNetworks}

                listData={networks}
                listHeaders={['code', 'name', 'mnemonic', 'sandreCode']}
                listTitle={i18n.networks}

                defaultSelectionList={networkHabilitations}
                selectionListHeaders={['code', 'name', 'mnemonic', 'sandreCode']}
                selectionListTitle={i18n.selectedNetworks}

                maxHeightTable={'50vh'}
                filterField={({
                    filter,
                    setFilter,
                }) => (
                    <Grid container spacing={'10px'} style={{ paddingTop: '5px' }}>
                        <Grid size={6}>
                            <Input
                                title={i18n.search}
                                value={filter.searchValue}
                                onChange={searchValue => setFilter(prev => ({ ...prev, searchValue }))}
                            />
                        </Grid>
                    </Grid>
                )}
                filterFunction={(list, { searchValue }) => {
                    const searchValueFormat = searchAllCharacters(searchValue)
                    return searchValue ? list.filter(p => searchAllCharacters(['code', 'name', 'mnemonic', 'sandreCode'].map(key => p[key])).includes(searchValueFormat)) : list
                }}
            />
        </>
    )
}

HabilitationAccordion.propTypes = {
    stationType: PropTypes.oneOf([PIEZO, QUALITO, PLUVIO, HYDRO, PRODUCTION, DISTRIBUTION, INSTALLATION, CATCHMENT, RESOURCE]),
    title: PropTypes.string,
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoInstallation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoDistributionUnit)),
        PropTypes.arrayOf(ProductionUnitShape),
        PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(ResourceDto)),
        // add catchment
    ]),
    noNetworkDisplay: PropTypes.bool,
    isLoaded: PropTypes.bool,

    habilitationsUser: PropTypes.arrayOf(PropTypes.shape({})),
    setHabilitationsUser: PropTypes.func,
    stationsUser: PropTypes.arrayOf(PropTypes.shape({})),
    setStationsUser: PropTypes.func,
}

export {
    BasicHabilitationAccordion,
    HabilitationAccordion,
}